import { internalFetch } from './fetchHelper';
import { getBackendApiUrl, getBackendApiKey } from './runtimeEnv';
import type { IPagedCollection } from './interfaces/IPagedCollection';

export interface IWorkOffer {
  id: number;
  workOfferBatchId: number;
  workId: number;
  contractorId: number;
  contractorName?: string;
  status: string;
  finalOfferedPrice?: number;
  agreedPrice?: number;
  responseNote?: string;
  respondedAt?: string;
  workAssignmentId?: number;
  createdAt: string;
  createdBy?: number;
}

export interface ICreateWorkOfferBatchRequest {
  workId: number;
  contractorIds: number[];
  message?: string;
  offeredPrice?: number;
}

export interface ICreateWorkOfferBatchResponse {
  workOfferBatchId: number;
  workOfferIds: number[];
}

export interface IAcceptWorkOfferRequest {
  responseNote?: string;
}

export interface IAcceptWorkOfferResponse {
  isSuccess: boolean;
  message?: string;
  correlationId?: string;
  workAssignmentId?: number | null;
}

export interface IRejectWorkOfferRequest {
  responseNote?: string;
}

export interface IRejectWorkOfferResponse {
  isSuccess: boolean;
  message?: string;
  correlationId?: string;
}

export type GetWorkOffersParams = {
  pageSize: number;
  pageNo: number;
  workId?: number;
  orderBy?: string;
  search?: string;
  filters?: string;
};

export type GetMyWorkOffersParams = {
  pageSize: number;
  pageNo: number;
  orderBy?: string;
  search?: string;
  filters?: string;
};

export const createWorkOfferBatch = async (
  abortController: AbortController,
  data: ICreateWorkOfferBatchRequest
): Promise<ICreateWorkOfferBatchResponse> => {
  return internalFetch(
    `${getBackendApiUrl()}/p/workOfferBatch`,
    'POST',
    abortController,
    JSON.stringify(data)
  );
};

export const getWorkOffers = async (
  abortController: AbortController,
  params: GetWorkOffersParams
): Promise<IPagedCollection<IWorkOffer>> => {
  const query = new URLSearchParams();
  query.set('pageSize', String(params.pageSize));
  query.set('pageNo', String(params.pageNo));
  if (params.workId != null) query.set('workId', String(params.workId));
  if (params.orderBy) query.set('orderBy', params.orderBy);
  if (params.search) query.set('search', params.search);
  if (params.filters) query.set('filters', params.filters);
  const code = getBackendApiKey();
  if (code) query.set('code', code);
  return internalFetch(
    `${getBackendApiUrl()}/p/workOffers?${query.toString()}`,
    'GET',
    abortController
  );
};

export const getMyWorkOffers = async (
  abortController: AbortController,
  params: GetMyWorkOffersParams
): Promise<IPagedCollection<IWorkOffer>> => {
  const query = new URLSearchParams();
  query.set('pageSize', String(params.pageSize));
  query.set('pageNo', String(params.pageNo));
  if (params.orderBy) query.set('orderBy', params.orderBy);
  if (params.search) query.set('search', params.search);
  if (params.filters) query.set('filters', params.filters);
  const code = getBackendApiKey();
  if (code) query.set('code', code);
  return internalFetch(
    `${getBackendApiUrl()}/p/me/workoffers?${query.toString()}`,
    'GET',
    abortController
  );
};

export const getWorkOffer = async (
  abortController: AbortController,
  workOfferId: number
): Promise<IWorkOffer> => {
  const code = getBackendApiKey();
  const codeQuery = code ? `&code=${code}` : '';
  return internalFetch(
    `${getBackendApiUrl()}/p/workOffer?workOfferId=${workOfferId}${codeQuery}`,
    'GET',
    abortController
  );
};

export const acceptWorkOffer = async (
  abortController: AbortController,
  workOfferId: number,
  data: IAcceptWorkOfferRequest
): Promise<IAcceptWorkOfferResponse> => {
  return internalFetch(
    `${getBackendApiUrl()}/p/workOffer/${workOfferId}/accept`,
    'POST',
    abortController,
    JSON.stringify(data)
  );
};

export const rejectWorkOffer = async (
  abortController: AbortController,
  workOfferId: number,
  data: IRejectWorkOfferRequest
): Promise<IRejectWorkOfferResponse> => {
  return internalFetch(
    `${getBackendApiUrl()}/p/workOffer/${workOfferId}/reject`,
    'POST',
    abortController,
    JSON.stringify(data)
  );
};

export const isWorkOfferPending = (status?: string): boolean =>
  (status ?? '').trim().toLowerCase() === 'pending';
